import { Array, Option, Schema } from 'effect';

const tokenDetails = { text: Schema.String, start: Schema.Int, end: Schema.Int };
export const tokenSchema = Schema.Union([
  Schema.Struct({
    kind: Schema.Literals([
      'word',
      'number',
      'relative',
      'op',
      'lparen',
      'rparen',
      'comma',
      'space',
    ]),
    ...tokenDetails,
  }),
  Schema.Struct({
    kind: Schema.Literal('string'),
    ...tokenDetails,
    quote: Schema.Literals(['open', 'closed']),
  }),
]);
export type Token = typeof tokenSchema.Type;
export type TokenKind = Token['kind'];

const operatorCharacters = ['!=', '>=', '<=', '!~', '=', '>', '<', '~'] as const;

const wordPattern = /^[A-Za-z_][A-Za-z0-9_.@+-]*/;
const numberPattern = /^-?\d+(\.\d+)?(?![A-Za-z])/;
const relativePattern = /^-\d+[dwmy]\b/;
const spacePattern = /^\s+/;

const readString = (input: string, start: number): Token => {
  const pattern = input[start] === '"' ? /^(?:[^"\\]|\\[\s\S])*"/ : /^(?:[^'\\]|\\[\s\S])*'/;
  const match = pattern.exec(input.slice(start + 1));
  const close = match === null ? -1 : start + match[0].length;
  const end = close === -1 ? input.length : close + 1;
  return {
    kind: 'string',
    text: input.slice(start, end),
    start,
    end,
    quote: close === -1 ? 'open' : 'closed',
  };
};

const readToken = (input: string, start: number): Token => {
  const rest = input.slice(start);
  const single = (kind: Exclude<TokenKind, 'string'>, length: number): Token => ({
    kind,
    text: rest.slice(0, length),
    start,
    end: start + length,
  });

  if (rest[0] === '"' || rest[0] === "'") return readString(input, start);
  if (rest[0] === '(') return single('lparen', 1);
  if (rest[0] === ')') return single('rparen', 1);
  if (rest[0] === ',') return single('comma', 1);

  const space = spacePattern.exec(rest);
  if (space) return single('space', space[0].length);

  const relative = relativePattern.exec(rest);
  if (relative) return single('relative', relative[0].length);

  const date = /^\d{4}-\d{2}-\d{2}\b/.exec(rest);
  if (date) return single('word', date[0].length);

  const number = numberPattern.exec(rest);
  if (number) return single('number', number[0].length);

  const op = operatorCharacters.find((candidate) => rest.startsWith(candidate));
  if (op) return single('op', op.length);

  const word = wordPattern.exec(rest);
  if (word) return single('word', word[0].length);

  return single('word', 1);
};

export const tokenize = (input: string): readonly Token[] =>
  Array.unfold(0, (start) => {
    if (start >= input.length) return Option.none();
    const token = readToken(input, start);
    return Option.some([token, token.end] as const);
  });

export const unquote = (text: string): string => {
  if (text[0] !== '"' && text[0] !== "'") return text;
  const end = text.length >= 2 && text.at(-1) === text[0] ? -1 : undefined;
  return text.slice(1, end).replace(/\\(.)/g, '$1');
};
